/**
 * AI Assist Settings Service — DB persistence for non-agent LLM capabilities.
 */

import { getSupabaseClient } from './supabaseClient';

const TABLE = 'ai_assist_settings';

export interface AiAssistSettingRow {
  workspaceId: string;
  capabilityId: string;
  enabled: boolean;
  updatedAt: string;
}

function rowToSetting(row: Record<string, unknown>): AiAssistSettingRow {
  return {
    workspaceId: row.workspace_id as string,
    capabilityId: row.capability_id as string,
    enabled: row.enabled as boolean,
    updatedAt: row.updated_at as string,
  };
}

export async function listAiAssistSettings(
  workspaceId: string,
): Promise<{ data: AiAssistSettingRow[]; error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from(TABLE)
      .select('*')
      .eq('workspace_id', workspaceId);

    if (error) return { data: [], error: error.message };
    return {
      data: (data ?? []).map((row) => rowToSetting(row as Record<string, unknown>)),
      error: null,
    };
  } catch (err) {
    return { data: [], error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

export async function upsertAiAssistSetting(input: {
  workspaceId: string;
  capabilityId: string;
  enabled: boolean;
  updatedBy?: string;
}): Promise<{ data: AiAssistSettingRow | null; error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase.rpc('upsert_ai_assist_setting', {
      p_workspace_id: input.workspaceId,
      p_capability_id: input.capabilityId,
      p_enabled: input.enabled,
      p_updated_by: input.updatedBy ?? null,
    });

    if (error) return { data: null, error: error.message };
    if (!data) return { data: null, error: 'No data returned from upsert' };

    return { data: rowToSetting(data as Record<string, unknown>), error: null };
  } catch (err) {
    return { data: null, error: err instanceof Error ? err.message : 'Unknown error' };
  }
}
